import React, { useMemo } from 'react';
import './MonthRangeSlider.css';

/** 'YYYYMM' -> absolute month index (year * 12 + monthIndex) */
export const monthKeyToIndex = (monthKey) => {
  const value = String(monthKey || '');
  if (!/^\d{6}$/.test(value)) return null;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6));
  if (month < 1 || month > 12) return null;
  return year * 12 + (month - 1);
};

/** absolute month index -> 'YYYYMM' */
export const indexToMonthKey = (index) => {
  const year = Math.floor(index / 12);
  const month = index - year * 12 + 1;
  return `${year}${String(month).padStart(2, '0')}`;
};

/** 'YYYYMM' -> 'MM/YYYY' */
export const formatMonthKey = (monthKey) => {
  const value = String(monthKey || '');
  if (!/^\d{6}$/.test(value)) return '';
  return `${value.slice(4, 6)}/${value.slice(0, 4)}`;
};

/**
 * Dual-handle month/year range slider.
 * Selecting the full available span emits [] (filter inactive).
 */
const MonthRangeSlider = ({ minMonth, maxMonth, selectedRange, onChange }) => {
  const minIndex = monthKeyToIndex(minMonth);
  const maxIndex = monthKeyToIndex(maxMonth);

  const [fromIndex, toIndex] = useMemo(() => {
    if (minIndex === null || maxIndex === null) return [null, null];

    const clamp = (index) => Math.min(maxIndex, Math.max(minIndex, index));
    const selectedFrom = Array.isArray(selectedRange) ? monthKeyToIndex(selectedRange[0]) : null;
    const selectedTo = Array.isArray(selectedRange) ? monthKeyToIndex(selectedRange[1]) : null;

    if (selectedFrom === null || selectedTo === null) return [minIndex, maxIndex];
    const from = clamp(Math.min(selectedFrom, selectedTo));
    const to = clamp(Math.max(selectedFrom, selectedTo));
    return [from, to];
  }, [minIndex, maxIndex, selectedRange]);

  if (minIndex === null || maxIndex === null) {
    return <div className="filters-accordion-empty">No options available</div>;
  }

  const span = maxIndex - minIndex;
  const isFullSpan = fromIndex === minIndex && toIndex === maxIndex;

  const emit = (from, to) => {
    if (typeof onChange !== 'function') return;
    if (from === minIndex && to === maxIndex) {
      onChange([]);
      return;
    }
    onChange([indexToMonthKey(from), indexToMonthKey(to)]);
  };

  const handleFromChange = (event) => {
    const next = Math.min(Number(event.target.value), toIndex);
    emit(next, toIndex);
  };

  const handleToChange = (event) => {
    const next = Math.max(Number(event.target.value), fromIndex);
    emit(fromIndex, next);
  };

  const toPercent = (index) => (span === 0 ? 0 : ((index - minIndex) / span) * 100);
  const leftPercent = toPercent(fromIndex);
  const rightPercent = toPercent(toIndex);

  return (
    <div className="month-range-slider">
      <div className="month-range-slider__header">
        <span className="month-range-slider__value">
          {`${formatMonthKey(indexToMonthKey(fromIndex))} - ${formatMonthKey(indexToMonthKey(toIndex))}`}
        </span>
        {!isFullSpan && (
          <button
            type="button"
            className="month-range-slider__reset"
            onClick={() => onChange && onChange([])}
            aria-label="Reset publication date filter"
          >
            &times;
          </button>
        )}
      </div>

      <div className="month-range-slider__track-wrapper">
        <div className="month-range-slider__track" />
        <div
          className="month-range-slider__selected"
          style={{ left: `${leftPercent}%`, width: `${Math.max(rightPercent - leftPercent, 0)}%` }}
        />
        <input
          type="range"
          className="month-range-slider__input month-range-slider__input--from"
          /* When both thumbs sit at the far right, the "from" thumb must stay grabbable */
          style={fromIndex === maxIndex ? { zIndex: 3 } : undefined}
          min={minIndex}
          max={maxIndex}
          step={1}
          value={fromIndex}
          onChange={handleFromChange}
          disabled={span === 0}
          aria-label="Publication date from"
        />
        <input
          type="range"
          className="month-range-slider__input month-range-slider__input--to"
          min={minIndex}
          max={maxIndex}
          step={1}
          value={toIndex}
          onChange={handleToChange}
          disabled={span === 0}
          aria-label="Publication date to"
        />
      </div>

      <div className="month-range-slider__scale">
        <span>{formatMonthKey(indexToMonthKey(minIndex))}</span>
        <span>{formatMonthKey(indexToMonthKey(maxIndex))}</span>
      </div>
    </div>
  );
};

export default MonthRangeSlider;
